
import {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
  useContext,
} from 'react';
import { useListState } from '@mantine/hooks';
import { useUserConfigState, useUserConfigDispatch } from '@/context/UserConfigContext';
import { type RateMultiplier, type ShortTurnConfig, type UserConfigurationState } from '@/types';
import { toast } from '@/components/ui/sonner';
import { MultipliersConfigForm } from '@/components/config_forms/multipliers/MultipliersConfigForm';
import { ValidationContext } from '@/components/validation';
import { getMultiplierUsageDetails } from '@/services/userConfigService';
import { DeletionDialog } from '@/components/ui/DeletionDialog';

import { shortTurnModes } from '@/components/config_forms/short_turn/utils/surchargeMethods';
import { premiumTypeOptions } from '@/constants/premiumShiftTypes';
import { holidayPremiumMethods } from '@/components/config_forms/holiday/components/HolidayPremiumMethodSelector';
import { initialOnboardingState } from '@/constants/initialState';
interface ResolutionValue {
  action: string;
  feature?: string;
  value?: string;
  requiresInput?: { type: 'number'; unit: string; position: 'left' | 'right' };
  inputValue?: number | null;
}

export interface MultipliersConfigProps {
  onValidityChange?: (valid: boolean) => void;
}

export interface MultipliersConfigHandle {
  /** `true` ⇢ user can advance/save; `false` ⇢ block Next/Save */
  isStepValid: () => boolean;
}

export const MultipliersConfig = forwardRef<MultipliersConfigHandle, MultipliersConfigProps>(
  ({ onValidityChange }, ref) => {
  const state = useUserConfigState();
  const { updateRateMultipliers: updateRateMultipliersInContext, updateBillingConfig, updateBillingStructureConfig } = useUserConfigDispatch();
  const { rateMultipliers: rateMultipliersFromContext, billingStructure, billingStructureConfig } = state;
  const { minimumBillableHours, weekStart, perWeekDayRateCalculationBasis } = billingStructureConfig || {};
  const { overtimeConcurrencyStrategy } = state;

  const [rateMultipliers, rateMultipliersHandlers] = useListState<RateMultiplier>(rateMultipliersFromContext || []);

  useEffect(() => {
    rateMultipliersHandlers.setState(rateMultipliersFromContext || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rateMultipliersHandlers is a Mantine useListState handler that changes identity each render; only sync when source data changes
  }, [rateMultipliersFromContext]);

  const [resolutions, setResolutions] = useState<Record<string, ResolutionValue>>({});

  const [deleteMultiplierDialogState, setDeleteMultiplierDialogState] = useState<{
    open: boolean;
    type: 'cannot_delete' | 'confirm_archive' | 'confirm_simple_delete';
    multiplierIndex: number | null;
    usageDetails: {
      has_booking_history: boolean;
      client_names_with_history: string[];
      client_names_config_only: string[];
      default_usages: string[];
    } | null;
  }>({ open: false, type: 'cannot_delete', multiplierIndex: null, usageDetails: null });

  const getResolutionOptions = useCallback((usageWithContext: string, multiplierToDelete: RateMultiplier | null): { label: string; value: string }[] => {
    const options: { label: string; value: string }[] = [];
    const allAlts = rateMultipliers.filter(m => m.code !== multiplierToDelete?.code && m.code !== 'STRAIGHT_TIME');
    const [feature, surchargeType] = usageWithContext.split(':');
    const featureKey = feature.toLowerCase();

    // 1. Filter alternative multipliers based on usage context
    let validAlts = allAlts;
    if (featureKey.includes('holiday premium') && surchargeType === 'offset_overtime') {
      const remainingThresholdMultipliers = allAlts.filter(m =>
          m.defaultPerShiftThresholdHours != null || m.defaultPerWeekThresholdHours != null
      );
      const highestThresholdRate = remainingThresholdMultipliers.length > 0
          ? Math.max(...remainingThresholdMultipliers.map(m => m.defaultMultiplier ?? 0))
          : 1;

      validAlts = allAlts.filter(m =>
          m.defaultPerShiftThresholdHours == null &&
          m.defaultPerWeekThresholdHours == null &&
          (m.defaultMultiplier ?? 0) > highestThresholdRate
      );
    }

    // Add valid multiplier replacements
    validAlts.forEach(alt => {
      options.push({
        label: `Replace with "${alt.name}" (${alt.defaultMultiplier}x)`,
        value: JSON.stringify({ action: 'change_multiplier', value: alt.code }),
      });
    });

    // 2. Add surcharge type change options
    const hasRemainingAlts = allAlts.length > 0;
    const hasRemainingAltsWithThresholds = allAlts.some(m =>
        m.defaultPerShiftThresholdHours != null || m.defaultPerWeekThresholdHours != null
    );

    if (featureKey.includes('short turn')) {
      shortTurnModes.forEach(opt => {
        const isCurrentlyUsed = opt.value === surchargeType;
        const isDisabled = (opt.value === "offset_overtime" && !hasRemainingAlts) || ((opt.value === "carry_over" || opt.value === "bump_up") && !hasRemainingAltsWithThresholds);
        if (!isCurrentlyUsed && !isDisabled) {
          const resolutionValue: ResolutionValue = { action: 'change_surcharge_type', feature: 'short_turn', value: opt.value };
          if (opt.value === 'fixed_dollar') {
            resolutionValue.requiresInput = { type: 'number', unit: '$', position: 'left' };
          }
          options.push({ label: `Change method to "${opt.label}"`, value: JSON.stringify(resolutionValue) });
        }
      });
    } else if (featureKey.includes('holiday premium')) {
      holidayPremiumMethods.forEach(opt => {
        const isCurrentlyUsed = opt.value === surchargeType;
        const isDisabled = (opt.value === 'offset_overtime' || opt.value === 'guaranteed_rate') && !hasRemainingAlts;
        if (!isCurrentlyUsed && !isDisabled) {
          const resolutionValue: ResolutionValue = { action: 'change_surcharge_type', feature: 'holiday', value: opt.value };
          if (opt.value === 'fixed_dollar') {
            resolutionValue.requiresInput = { type: 'number', unit: '$', position: 'left' };
          }
          options.push({ label: `Change method to "${opt.label}"`, value: JSON.stringify(resolutionValue) });
        }
      });
    } else if (featureKey.includes('premium shift')) {
      premiumTypeOptions.forEach(opt => {
        const isCurrentlyUsed = opt.value === surchargeType;
        const isDisabled = opt.value === "multiplier_offset" && !hasRemainingAlts;
        if (!isCurrentlyUsed && !isDisabled) {
          const resolutionValue: ResolutionValue = { action: 'change_surcharge_type', feature: 'premium_shift', value: opt.value ?? undefined };
          if (opt.value === 'fixed') {
            resolutionValue.requiresInput = { type: 'number', unit: '$', position: 'left' };
          }
          options.push({ label: `Change method to "${opt.label}"`, value: JSON.stringify(resolutionValue) });
        }
      });
    }

    // 3. Add disable option
    let disableLabel = 'Disable this surcharge feature';
    if (featureKey.includes('holiday premium') && surchargeType === 'offset_overtime') {
      disableLabel = 'Disable Extended Overtime only';
    }
    options.push({
      label: disableLabel,
      value: JSON.stringify({ action: 'disable' }),
    });

    return options;
  }, [rateMultipliers]);

  const handleWeekStartChange = (value: number) => {
    updateBillingStructureConfig({ weekStart: value });
  };

  const handleResolutionChange = (usage: string, resolutionPart: Partial<ResolutionValue>) => {
    setResolutions(prev => {
      const existingResolution: Partial<ResolutionValue> = prev[usage] || {};

      // If resolutionPart has an action, it's a full change of resolution type from the dropdown
      if (resolutionPart.action) {
        const newResolution = { ...resolutionPart }; // Start with the full object from the option
        if (newResolution.requiresInput) {
          // If the old one had a value, keep it, otherwise default.
          newResolution.inputValue = existingResolution.inputValue ?? 50;
        }
        return { ...prev, [usage]: newResolution as ResolutionValue };
      }

      // Otherwise, it's just a partial update (like inputValue from the NumberInput)
      else {
        return { ...prev, [usage]: { ...existingResolution, ...resolutionPart } as ResolutionValue };
      }
    });
  };

  const applyResolutionsAndRetryDelete = async () => {
    const multiplierIndex = deleteMultiplierDialogState.multiplierIndex;
    if (multiplierIndex === null) return;

    // Collect all state updates into a single object to avoid race conditions
    const updates: Partial<UserConfigurationState> = {};

    Object.entries(resolutions).forEach(([usageWithContext, resolution]) => {
      const [feature, surchargeType] = usageWithContext.split(':');
      const featureKey = feature.toLowerCase();
      if (resolution.action === 'disable') {
        if (featureKey.includes('short turn')) {
          updates.usesShortTurn = false;
        } else if (featureKey.includes('holiday premium')) {
          updates.applyHolidayPremium = false;
        } else if (featureKey.includes('premium shift')) {
          updates.allowPremiumShifts = false;
        }
      } else if (resolution.action === 'change_multiplier' && resolution.value) {
        const newMultiplierCode = resolution.value;
        if (featureKey.includes('short turn')) {
          const currentConfig = state.shortTurnConfig || initialOnboardingState.shortTurnConfig!;
          updates.shortTurnConfig = { ...currentConfig, ...updates.shortTurnConfig, rateMultiplierCode: newMultiplierCode } as ShortTurnConfig;
        } else if (featureKey.includes('premium shift')) {
          updates.premiumShiftMultiplierCode = newMultiplierCode;
        }
      } else if (resolution.action === 'change_surcharge_type' && resolution.feature && resolution.value) {
        const newSurchargeType = resolution.value;
        if (resolution.feature === 'short_turn') {
          const currentConfig = state.shortTurnConfig || initialOnboardingState.shortTurnConfig!;
          updates.shortTurnConfig = { ...currentConfig, ...updates.shortTurnConfig, surchargeType: newSurchargeType as ShortTurnConfig['surchargeType'], rateMultiplierCode: null } as ShortTurnConfig;
          if (newSurchargeType === 'fixed_dollar') {
            updates.shortTurnConfig!.shortTurnFixedSurchargeAmount = resolution.inputValue ?? null;
          }
        } else if (resolution.feature === 'holiday') {
          updates.holidayPremiumType = newSurchargeType as UserConfigurationState['holidayPremiumType'];
          if (newSurchargeType === 'fixed_dollar') {
            updates.holidayFixedPremiumAmount = resolution.inputValue;
          }
        } else if (resolution.feature === 'premium_shift') {
          updates.premiumShiftOffsetMode = newSurchargeType as UserConfigurationState['premiumShiftOffsetMode'];
          if (newSurchargeType === 'fixed') {
            updates.premiumShiftFixedOffset = resolution.inputValue;
          }
        }
      }
    });

    // Explicitly sanitize holiday fields based on the final surcharge type to ensure consistency
    // This is important because multiple holiday resolutions might have been applied.
    const finalHolidayPremiumType = updates.holidayPremiumType || state.holidayPremiumType;
    if (updates.applyHolidayPremium === false) {
      updates.holidayPremiumType = 'offset_overtime';
      updates.holidayFixedPremiumAmount = null;
    } else if (finalHolidayPremiumType) {
        if (finalHolidayPremiumType !== 'fixed_dollar') {
            updates.holidayFixedPremiumAmount = null;
        }
    }

    if (updates.shortTurnConfig) {
      const finalSTSurchargeType = updates.shortTurnConfig.surchargeType;
      if (finalSTSurchargeType !== 'offset_overtime' && finalSTSurchargeType !== 'carry_over' && finalSTSurchargeType !== 'bump_up') {
        updates.shortTurnConfig.floorTierCode = null;
      }
      if (finalSTSurchargeType !== 'fixed_dollar') {
        updates.shortTurnConfig.shortTurnFixedSurchargeAmount = null;
      }
    }

    if (updates.premiumShiftOffsetMode) {
      const finalPSSurchargeType = updates.premiumShiftOffsetMode;
      if (finalPSSurchargeType !== 'fixed') {
        updates.premiumShiftFixedOffset = null;
      }
    }

    if (Object.keys(updates).length > 0) {
      updateBillingConfig(updates);
    }

    setDeleteMultiplierDialogState({ open: false, type: 'cannot_delete', multiplierIndex: null, usageDetails: null });
    setResolutions({});

    await new Promise(resolve => setTimeout(resolve, 100));

    performDeletion(multiplierIndex);
  };

  const performDeletion = (indexToRemove: number) => {
    const multiplierToRemove = rateMultipliers[indexToRemove];
    const updatedMultipliers = rateMultipliers.filter((_, i) => i !== indexToRemove);
    rateMultipliersHandlers.setState(updatedMultipliers);
    updateRateMultipliersInContext(updatedMultipliers);
    toast.success(`Multiplier "${multiplierToRemove.name}" removed.`);
  };

  const confirmRemove = () => {
    if (deleteMultiplierDialogState.multiplierIndex !== null) {
      performDeletion(deleteMultiplierDialogState.multiplierIndex);
    }
    setDeleteMultiplierDialogState({ open: false, type: 'cannot_delete', multiplierIndex: null, usageDetails: null });
  };

  const handleRemove = async (indexToRemove: number) => {
    const multiplierToRemove = rateMultipliers[indexToRemove];

    // Check for usage before deleting
    try {
      const usageDetails = await getMultiplierUsageDetails(multiplierToRemove.code);
      const hasDefaultUsage = usageDetails.default_usages.length > 0;
      const clientsWithHistory = usageDetails.client_names_with_history || [];

      // A true hard block is usage in defaults. This requires user action to resolve.
      if (hasDefaultUsage) {
        const initialResolutions: Record<string, ResolutionValue> = {};
        (usageDetails.default_usages || []).forEach(usage => {
          const options = getResolutionOptions(usage, multiplierToRemove);
          if (options.length > 0) {
            const firstOption = JSON.parse(options[0].value);
            if (firstOption.requiresInput) {
              firstOption.inputValue = 50;
            }
            initialResolutions[usage] = firstOption;
          } else {
            initialResolutions[usage] = { action: 'disable' };
          }
        });
        setResolutions(initialResolutions);
        setDeleteMultiplierDialogState({ open: true, type: 'cannot_delete', multiplierIndex: indexToRemove, usageDetails });
        return;
      }

      // If no default usage, but there is historical usage, it should be archived.
      if (clientsWithHistory.length > 0) {
        setDeleteMultiplierDialogState({ open: true, type: 'confirm_archive', multiplierIndex: indexToRemove, usageDetails });
        return;
      }

      // If no default usage and no history, show confirmation dialog before deletion
      setDeleteMultiplierDialogState({ open: true, type: 'confirm_simple_delete', multiplierIndex: indexToRemove, usageDetails: null });

    } catch (error: unknown) {
      toast.error('Could not check multiplier usage', { description: error instanceof Error ? error.message : String(error) });
      return;
    }
  };

  /* ---------- validity helpers ---------- */
  const validationCtx = useContext(ValidationContext);
  // Schema-level multiplier validation is handled by the parent's ValidationProvider.
  // Check for multiplier-specific errors in any scope.
  const isValid = validationCtx
    ? !validationCtx.getActiveErrors().some(e => e.field.startsWith('rateMultipliers') || e.field.startsWith('minimumBillableHours'))
    : true;

  /* ---------- expose validity to parent ---------- */
  useImperativeHandle(ref, () => ({ isStepValid: () => isValid }));

  useEffect(() => {
    onValidityChange?.(isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onValidityChange is an optional callback prop that may change identity; only notify when validity actually changes
  }, [isValid]);

  return (
    <div id="multipliers-step" className="animate-fade-in w-full">
      <MultipliersConfigForm
        title="Overtime Multipliers"
        description="Set your preferred overtime rate multiplier progression."
        rateMultipliers={rateMultipliers}
        onMultipliersChange={(newMultipliers) => {
          rateMultipliersHandlers.setState(newMultipliers);
          updateRateMultipliersInContext(newMultipliers);
        }}
        billingStructure={billingStructure}
        minimumBillableHours={minimumBillableHours}
        onRemove={handleRemove}
        // Work Week Props
        weekStart={weekStart}
        onWeekStartChange={handleWeekStartChange}
        weekStartError={undefined}
        context="user"
        overtimeConcurrencyStrategy={overtimeConcurrencyStrategy}
        onOvertimeConcurrencyStrategyChange={(value) => updateBillingConfig({ overtimeConcurrencyStrategy: value })}
        perWeekDayRateCalculationBasis={perWeekDayRateCalculationBasis}
        onPerWeekDayRateCalculationBasisChange={(value) => updateBillingStructureConfig({ perWeekDayRateCalculationBasis: value })}
        parentState={state}
        noCard={true}
      />

      <DeletionDialog
        open={deleteMultiplierDialogState.open}
        onOpenChange={(isOpen) => !isOpen && setDeleteMultiplierDialogState({ open: false, type: 'cannot_delete', multiplierIndex: null, usageDetails: null })}
        type={deleteMultiplierDialogState.type}
        itemType="multiplier"
        itemIndex={deleteMultiplierDialogState.multiplierIndex}
        items={rateMultipliers}
        usageDetails={deleteMultiplierDialogState.usageDetails}
        resolutions={resolutions}
        onResolutionChange={handleResolutionChange}
        onConfirmDelete={confirmRemove}
        onApplyResolutionsAndDelete={applyResolutionsAndRetryDelete}
        getAlternativeMultipliers={getResolutionOptions}
      />
    </div>
  );
});
